/**
 * kordAI 음정(interval) 판별 — 백병동 화성학 §1-1 "음정, 음계, 조성" 대응.
 * backend/interval_theory.py 와 동일 로직. 화음 판정 엔진(harmonyEngine.js)과는 독립적인 유틸리티.
 */

const PITCH_RE = /^([A-G])(#{1,2}|b{1,2}|x|n)?(-?\d+)$/

const LETTER_INDEX = { C: 0, D: 1, E: 2, F: 3, G: 4, A: 5, B: 6 }
const NATURAL_PC = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 }
const ACCIDENTAL_SEMITONES = { '': 0, n: 0, '#': 1, '##': 2, x: 2, b: -1, bb: -2 }

// 생성음정 크기(0=1도...6=7도) → 옥타브 내 기준(장/완전) 반음 수
const REFERENCE_SEMITONES = [0, 2, 4, 5, 7, 9, 11]
// 완전음정 계열: 1도(0)·4도(3)·5도(4)
const PERFECT_SIZES = new Set([0, 3, 4])

const PERFECT_QUALITY = {
  '-2': ['겹감', 'double diminished'],
  '-1': ['감', 'diminished'],
  0: ['완전', 'perfect'],
  1: ['증', 'augmented'],
  2: ['겹증', 'double augmented'],
}
const IMPERFECT_QUALITY = {
  '-3': ['겹감', 'double diminished'],
  '-2': ['감', 'diminished'],
  '-1': ['단', 'minor'],
  0: ['장', 'major'],
  1: ['증', 'augmented'],
  2: ['겹증', 'double augmented'],
}

function parsePitch(name) {
  const m = String(name).trim().match(PITCH_RE)
  if (!m) throw new Error(`음이름을 해석할 수 없습니다: ${name}`)
  const letter = m[1]
  const acc = m[2] || ''
  const octave = parseInt(m[3], 10)
  return { letter, accidental: ACCIDENTAL_SEMITONES[acc], octave }
}

function diatonicStep(letter, octave) {
  return octave * 7 + LETTER_INDEX[letter]
}

function semitoneAbs(letter, accidental, octave) {
  return (octave + 1) * 12 + NATURAL_PC[letter] + accidental
}

function ordinalSuffix(n) {
  const m = n % 100
  if (m >= 10 && m <= 20) return 'th'
  return { 1: 'st', 2: 'nd', 3: 'rd' }[n % 10] ?? 'th'
}

/**
 * @param {string} noteA 과학적 음이름 (예: "C4", "Cb4", "A#4", "Fbb3", "Gx5")
 * @param {string} noteB
 * @returns {{ number: number, genericSize: number, semitones: number, qualityKo: string, qualityEn: string, label: string, labelEn: string, direction: 'ascending'|'descending' }}
 */
export function intervalBetween(noteA, noteB) {
  let a = parsePitch(noteA)
  let b = parsePitch(noteB)

  let stepA = diatonicStep(a.letter, a.octave)
  let stepB = diatonicStep(b.letter, b.octave)
  const direction = stepB >= stepA ? 'ascending' : 'descending'
  if (stepB < stepA) {
    ;[a, b] = [b, a]
    ;[stepA, stepB] = [stepB, stepA]
  }

  const diatonicDiff = stepB - stepA
  const genericSize = diatonicDiff % 7
  const octaves = Math.floor(diatonicDiff / 7)

  const actualSemitones = semitoneAbs(b.letter, b.accidental, b.octave) - semitoneAbs(a.letter, a.accidental, a.octave)
  const naturalSemitones = octaves * 12 + REFERENCE_SEMITONES[genericSize]
  const delta = actualSemitones - naturalSemitones

  const table = PERFECT_SIZES.has(genericSize) ? PERFECT_QUALITY : IMPERFECT_QUALITY
  const found = table[String(delta)]
  const [qualityKo, qualityEn] = found ?? [`${delta >= 0 ? '+' : ''}${delta}단계`, `${delta >= 0 ? '+' : ''}${delta} step`]

  const number = genericSize + 1 + octaves * 7

  return {
    number,
    genericSize,
    semitones: actualSemitones,
    qualityKo,
    qualityEn,
    label: `${qualityKo}${number}도`,
    labelEn: `${qualityEn} ${number}${ordinalSuffix(number)}`,
    direction,
  }
}

/** 생성음정 크기(0~6)·성질 조합으로 나올 수 있는 모든 (도수, 반음수) 라벨 (1옥타브 이내, 0~12반음). */
function buildAllIntervalLabels() {
  const out = []
  for (let genericSize = 0; genericSize < 7; genericSize++) {
    const table = PERFECT_SIZES.has(genericSize) ? PERFECT_QUALITY : IMPERFECT_QUALITY
    for (const [deltaStr, [qualityKo, qualityEn]] of Object.entries(table)) {
      const delta = Number(deltaStr)
      const semitones = REFERENCE_SEMITONES[genericSize] + delta
      if (semitones < 0 || semitones > 12) continue
      const number = genericSize + 1
      out.push({
        number,
        semitones,
        qualityKo,
        qualityEn,
        label: `${qualityKo}${number}도`,
        labelEn: `${qualityEn} ${number}${ordinalSuffix(number)}`,
      })
    }
  }
  return out
}

const ALL_INTERVAL_LABELS = buildAllIntervalLabels()

/**
 * 실제 반음수가 같은 다른 도수/이름의 음정들을 찾는다 (이명동음정).
 * 예: enharmonicEquivalents(6) -> 증4도, 감5도
 *     enharmonicEquivalents(11) -> 장7도, 겹증6도
 * 옥타브(0~12) 이내로 한정 — 복합음정(9도 이상)은 다루지 않음.
 */
export function enharmonicEquivalents(semitones, excludeLabel = null) {
  const s = semitones !== 12 ? ((semitones % 12) + 12) % 12 : 12
  return ALL_INTERVAL_LABELS.filter((item) => item.semitones === s && item.label !== excludeLabel)
}

/** intervalBetween 결과 + 같은 소리의 다른 이름들(enharmonic). */
export function intervalWithEnharmonics(noteA, noteB) {
  const result = intervalBetween(noteA, noteB)
  result.enharmonic = enharmonicEquivalents(result.semitones, result.label)
  return result
}

// ─── 협화음정 / 불협화음정 (백병동 §1-1) ───────────────────────────────────
// 완전협화: 완전1도·완전4도·완전5도·완전8도 (백병동 기준 — 완전4도도 완전협화에 포함)
// 불완전협화: 장/단 3도·6도
// 불협화: 2도·7도 전부 + 모든 증·감(겹증·겹감 포함) 음정

export const CONSONANCE_PERFECT = '완전협화'
export const CONSONANCE_IMPERFECT = '불완전협화'
export const CONSONANCE_DISSONANT = '불협화'

// 불완전협화 대상: 3도(2)·6도(5)만 해당 — 2도(1)·7도(6)는 장/단이어도 불협화.
const IMPERFECT_CONSONANCE_SIZES = new Set([2, 5])

/** intervalBetween()의 genericSize·qualityKo로 협화/불협화 분류. */
export function classifyConsonance(genericSize, qualityKo) {
  if (PERFECT_SIZES.has(genericSize)) {
    return qualityKo === '완전' ? CONSONANCE_PERFECT : CONSONANCE_DISSONANT
  }
  if (IMPERFECT_CONSONANCE_SIZES.has(genericSize)) {
    return qualityKo === '장' || qualityKo === '단' ? CONSONANCE_IMPERFECT : CONSONANCE_DISSONANT
  }
  return CONSONANCE_DISSONANT
}

/** intervalWithEnharmonics 결과 + consonance 필드. */
export function intervalFull(noteA, noteB) {
  const result = intervalWithEnharmonics(noteA, noteB)
  result.consonance = classifyConsonance(result.genericSize, result.qualityKo)
  return result
}

// ─── 두 성부의 동시 진행 유형 (백병동 §2-2) ─────────────────────────────────
export const MOTION_PARALLEL = '병진행'
export const MOTION_SIMILAR = '유사진행'
export const MOTION_CONTRARY = '반진행'
export const MOTION_OBLIQUE = '사진행'
export const MOTION_STATIC = '정지'

/**
 * 두 성부의 이전→현재 MIDI 값으로 병진행/유사진행/반진행/사진행을 판별.
 * - 병진행: 같은 방향 + 같은 반음수만큼 이동(두 성부 사이 음정이 그대로 유지됨)
 * - 유사진행: 같은 방향인데 이동한 반음수가 다름(음정이 변함)
 * - 반진행: 서로 반대 방향
 * - 사진행: 한쪽은 그대로, 다른 쪽만 움직임
 * - 정지: 둘 다 안 움직임
 */
export function motionType(prevA, currA, prevB, currB) {
  const dA = currA - prevA
  const dB = currB - prevB
  if (dA === 0 && dB === 0) return MOTION_STATIC
  if (dA === 0 || dB === 0) return MOTION_OBLIQUE
  if ((dA > 0) !== (dB > 0)) return MOTION_CONTRARY
  return dA === dB ? MOTION_PARALLEL : MOTION_SIMILAR
}
